import { UserMenu } from "@/components/CodeGen";
import { Button } from "@/components/ui/button";
import { useHandler } from "@/hooks";
import { cn } from "@/lib/utils";
import { useSidebarStore } from "@/state/sidebarStore";
import { ChevronLeftIcon } from "@radix-ui/react-icons";
import { useRouter } from "@tanstack/react-router";
import { type PropsWithChildren, memo, useEffect, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useShallow } from "zustand/react/shallow";
import { RecentItems } from "../CodeGen";
import { Logo } from "./logo";

export function CodeGenLayout({ children }: PropsWithChildren) {
  const { isFloating, isCollapsed } = useSidebarStore(
    useShallow(({ isFloating, isCollapsed }) => ({
      isFloating,
      isCollapsed,
    })),
  );
  return (
    <div className="flex flex-col w-full h-screen bg-background">
      <Sidebar />
      {/* Main content: margin in attached mode, full width in floating */}
      <div
        className={cn(
          "h-full",
          !isFloating && (isCollapsed ? "ml-[60px]" : "ml-[240px]"),
        )}
      >
        {children}
      </div>
    </div>
  );
}

const Sidebar = () => {
  const {
    isFloating,
    isSidebarVisible,
    isCollapsed,
    setIsFloating,
    setIsSidebarVisible,
    setIsCollapsed,
  } = useSidebarStore(
    useShallow(
      ({
        isFloating,
        isSidebarVisible,
        isCollapsed,
        setIsFloating,
        setIsSidebarVisible,
        setIsCollapsed,
      }) => ({
        isFloating,
        isSidebarVisible,
        isCollapsed,
        setIsFloating,
        setIsSidebarVisible,
        setIsCollapsed,
      }),
    ),
  );

  const toggleSidebar = useHandler(() => {
    if (isFloating) {
      setIsSidebarVisible(!isSidebarVisible);
    } else {
      setIsCollapsed(!isCollapsed);
      // Detach when collapsing
      if (!isCollapsed) {
        setIsFloating(true);
        setIsSidebarVisible(false);
        setIsCollapsed(true);
      }
    }
  });

  const onHover = useHandler(() => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
    setIsSidebarVisible(true);
  });

  const onHide = useHandler(() => {
    if (isFloating && isCollapsed) {
      hideTimeout.current = setTimeout(() => {
        setIsSidebarVisible(false);
      }, 300);
    }
  });

  useHotkeys(
    "mod+b",
    (e) => {
      e.preventDefault();
      toggleSidebar();
    },
    { enableOnFormTags: true },
  );

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (isFloating && !isSidebarVisible && isCollapsed && e.clientX < 32) {
        setIsSidebarVisible(true);
      }
    }
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [isFloating, isSidebarVisible, isCollapsed, setIsSidebarVisible]);

  const hideTimeout = useRef<NodeJS.Timeout | null>(null);

  return (
    <>
      {/* Hotspot for showing sidebar when detached and closed */}
      {isFloating && !isSidebarVisible && (
        <div
          className="fixed top-0 left-0 h-full w-8 z-40 cursor-pointer"
          onMouseEnter={onHover}
        />
      )}
      {/* Sidebar overlay, always fixed */}
      <div
        className={cn(
          "fixed top-0 left-0 h-full z-50 shadow-lg transition-left duration-300 w-[240px]",
          !isFloating || isSidebarVisible ? "left-0" : "-left-[240px]",
          !isFloating && isCollapsed && "w-[60px]",
        )}
      >
        <SidebarContent
          onHover={onHover}
          onHide={onHide}
          toggleSidebar={toggleSidebar}
        />
      </div>
    </>
  );
};

const SidebarContent = memo(
  (props: {
    onHover: () => void;
    onHide: () => void;
    toggleSidebar: () => void;
  }) => {
    const { onHover, onHide, toggleSidebar } = props;
    const { isFloating, isSidebarVisible, isCollapsed } = useSidebarStore(
      useShallow(({ isFloating, isSidebarVisible, isCollapsed }) => ({
        isFloating,
        isSidebarVisible,
        isCollapsed,
      })),
    );
    const router = useRouter();
    const handleNewChat = () => {
      router.navigate({ to: "/codegen" });
    };

    return (
      <div
        className="h-full flex flex-col bg-popover w-[240px] border-r"
        onMouseLeave={onHide}
        onMouseEnter={onHover}
      >
        <header className="shrink-0">
          <div className="flex items-center justify-between h-12 px-4">
            <Logo />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              title="Toggle sidebar (⌘B)"
            >
              <ChevronLeftIcon
                className={cn(
                  "h-4 w-4 transition-transform",
                  isFloating &&
                    !isSidebarVisible &&
                    isCollapsed &&
                    "rotate-180",
                  !isFloating && isCollapsed && "rotate-180",
                )}
              />
            </Button>
          </div>
        </header>
        <div className="flex-1 flex flex-col min-h-0">
          <RecentItems />

          <Button
            className="mx-4"
            size="sm"
            variant="primary"
            onClick={handleNewChat}
          >
            New Chat
          </Button>
          <UserMenu />
        </div>
      </div>
    );
  },
);
